import React, { useEffect, useState } from "react";
import api, { fmtTL, todayISO } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Wallet, ChevronLeft, ChevronRight, FileDown } from "lucide-react";

const CAT_COLORS = {
  "Aidat": "bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400",
  "Elektrik": "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
  "Doğalgaz": "bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400",
  "Su": "bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-400",
  "Market": "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400",
  "Yakıt": "bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400",
  "Okul Servisi": "bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-400",
  "Okul Yemeği": "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/10 dark:text-fuchsia-400",
  "Kredi Kartı": "bg-slate-200 text-slate-700 dark:bg-slate-500/10 dark:text-slate-300",
  "İnternet": "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-400",
  "Telefon": "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-400",
  "Sigorta": "bg-teal-100 text-teal-700 dark:bg-teal-500/10 dark:text-teal-400",
  "Sağlık / İlaç": "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400",
  "Ulaşım": "bg-lime-100 text-lime-700 dark:bg-lime-500/10 dark:text-lime-400",
  "Giyim": "bg-pink-100 text-pink-700 dark:bg-pink-500/10 dark:text-pink-400",
  "Abonelikler": "bg-violet-100 text-violet-700 dark:bg-violet-500/10 dark:text-violet-400",
  "Restoran / Eğlence": "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-400",
  "Kuaför / Kişisel Bakım": "bg-stone-200 text-stone-700 dark:bg-stone-500/10 dark:text-stone-300",
  "Ev Bakım / Onarım": "bg-zinc-200 text-zinc-700 dark:bg-zinc-500/10 dark:text-zinc-300",
  "Eğitim / Kurs": "bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400",
  "Diğer": "bg-secondary text-muted-foreground",
};

const fmtMonthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
};
const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const EMPTY_FORM = { category: "", amount: "", date: todayISO(), description: "" };

export default function PersonalExpenses() {
  const [categories, setCategories] = useState([]);
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false); // false=closed, {}=new, {...}=edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [delTarget, setDelTarget] = useState(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [range, setRange] = useState({ start: "", end: "" });

  useEffect(() => { api.get("/personal/expenses/categories").then((r) => setCategories(r.data)); }, []);

  const load = () => {
    setLoading(true);
    api.get("/personal/expenses", { params: { month } }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [month]);

  const openNew = () => {
    setEditing({});
    const isCurrentMonth = month === todayISO().slice(0, 7);
    setForm({ ...EMPTY_FORM, date: isCurrentMonth ? todayISO() : `${month}-01` });
  };
  const openEdit = (item) => { setEditing(item); setForm({ category: item.category, amount: String(item.amount), date: item.date, description: item.description || "" }); };

  const save = async () => {
    if (!form.category) return toast.error("Kategori seçin");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Geçerli bir tutar girin");
    if (!form.date) return toast.error("Tarih seçin");
    try {
      if (editing?.id) {
        await api.put(`/personal/expenses/${editing.id}`, form);
        toast.success("Gider güncellendi");
      } else {
        await api.post("/personal/expenses", form);
        toast.success("Gider eklendi");
      }
      setEditing(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/personal/expenses/${delTarget.id}`); toast.success("Gider silindi"); setDelTarget(null); load(); }
    catch { toast.error("Silinemedi"); }
  };

  const downloadPdf = async (params, label) => {
    try {
      const res = await api.get("/personal/expenses/pdf", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Kisisel_Gider_Dokumu_${label}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF indirildi");
    } catch { toast.error("PDF oluşturulamadı"); }
  };

  const downloadThisMonth = () => downloadPdf({ month }, month);

  const downloadRange = () => {
    if (!range.start || !range.end) return toast.error("Başlangıç ve bitiş tarihi seçin");
    if (range.start > range.end) return toast.error("Başlangıç tarihi bitişten sonra olamaz");
    downloadPdf({ start: range.start, end: range.end }, `${range.start}_${range.end}`);
    setRangeOpen(false);
  };

  const total = items.reduce((s, i) => s + i.amount, 0);
  const byCategory = {};
  items.forEach((i) => { byCategory[i.category] = (byCategory[i.category] || 0) + i.amount; });
  const catBreakdown = Object.entries(byCategory).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" onClick={() => setMonth((m) => shiftMonth(m, -1))}><ChevronLeft size={16} /></Button>
          <div className="text-sm font-medium w-36 text-center capitalize">{fmtMonthLabel(month)}</div>
          <Button size="icon" variant="outline" onClick={() => setMonth((m) => shiftMonth(m, 1))}><ChevronRight size={16} /></Button>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="pe-pdf-btn"><FileDown size={16} className="mr-1.5" /> PDF İndir</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="pe-pdf-month" onClick={downloadThisMonth}>{fmtMonthLabel(month)} - Bu Ayı İndir</DropdownMenuItem>
              <DropdownMenuItem data-testid="pe-pdf-range" onClick={() => { setRange({ start: `${month}-01`, end: todayISO() }); setRangeOpen(true); }}>Tarih Aralığı Seç...</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button data-testid="pe-new-btn" onClick={openNew}><Plus size={16} className="mr-1.5" /> Yeni Gider</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Bu Ayki Toplam Gider</div>
          <div className="font-head text-2xl font-semibold mt-1 flex items-center gap-2"><Wallet size={20} className="text-primary" />{fmtTL(total)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Kategoriye Göre Dağılım</div>
          {catBreakdown.length === 0 && <div className="text-sm text-muted-foreground">Bu ay için kayıt yok</div>}
          <div className="flex flex-wrap gap-1.5">
            {catBreakdown.map(([c, amt]) => (
              <span key={c} className={`text-xs px-2 py-1 rounded ${CAT_COLORS[c] || CAT_COLORS["Diğer"]}`}>{c}: {fmtTL(amt)}</span>
            ))}
          </div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2.5 font-medium text-left">Tarih</th>
                <th className="px-3 py-2.5 font-medium text-left">Kategori</th>
                <th className="px-3 py-2.5 font-medium text-left">Açıklama</th>
                <th className="px-3 py-2.5 font-medium text-right">Tutar</th>
                <th className="px-3 py-2.5 font-medium text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">Yükleniyor...</td></tr>}
              {!loading && items.map((i) => (
                <tr key={i.id} data-testid={`pe-row-${i.id}`} className="border-t border-border hover:bg-accent/40">
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{i.date.split("-").reverse().join(".")}</td>
                  <td className="px-3 py-2.5"><Badge className={`border-0 ${CAT_COLORS[i.category] || CAT_COLORS["Diğer"]}`}>{i.category}</Badge></td>
                  <td className="px-3 py-2.5 text-muted-foreground">{i.description || "-"}</td>
                  <td className="px-3 py-2.5 text-right font-medium">{fmtTL(i.amount)}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button data-testid={`pe-edit-${i.id}`} title="Düzenle" onClick={() => openEdit(i)} className="text-muted-foreground hover:text-blue-500"><Pencil size={15} /></button>
                      <button data-testid={`pe-delete-${i.id}`} title="Sil" onClick={() => setDelTarget(i)} className="text-muted-foreground hover:text-rose-500"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">Bu ay için gider kaydı yok.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Gideri Düzenle" : "Yeni Gider"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-1">
            <div className="space-y-1.5 col-span-2"><Label className="text-xs">Kategori *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="pe-input-category"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Tutar (TL) *</Label>
              <Input data-testid="pe-input-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Tarih *</Label>
              <Input data-testid="pe-input-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="space-y-1.5 col-span-2"><Label className="text-xs">Açıklama (opsiyonel)</Label>
              <Input data-testid="pe-input-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)}>İptal</Button>
            <Button data-testid="pe-save" onClick={save}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rangeOpen} onOpenChange={setRangeOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tarih Aralığı Seç</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-1">
            <div className="space-y-1.5"><Label className="text-xs">Başlangıç</Label>
              <Input data-testid="pe-range-start" type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} />
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Bitiş</Label>
              <Input data-testid="pe-range-end" type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRangeOpen(false)}>İptal</Button>
            <Button data-testid="pe-range-download" onClick={downloadRange}><FileDown size={16} className="mr-1.5" /> PDF İndir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gideri sil</AlertDialogTitle>
            <AlertDialogDescription><strong>{delTarget?.category}</strong> ({delTarget && fmtTL(delTarget.amount)}) kaydı silinecek. Devam edilsin mi?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="pe-confirm-delete" onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
